import { HTMLAttributes, ReactNode, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { TFunction } from 'i18next'
import { useSortable } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { v4 as uuid } from 'uuid'
import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
} from 'app/frontend/types/custom/RaidPlan'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import TeamPlanAssignmentScopeCard from './TeamPlanAssignmentScopeCard'
import TeamPlanAssignmentVisualCard from './TeamPlanAssignmentVisualCard'
import { RaidstratsPlayer } from './RaidstratsContainer'
import useAbilityVisualPlanData from '../hooks/useAbilityVisualPlanData'
import AbilityVisualPopover from './AbilityVisualPopover'
import {
  AssignmentViewMode,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentViewMode,
  getAssignmentScopes,
  getSoakPrefix,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from './assignmentTimeline'
import { getAssignmentEntityLabel } from './assignmentEntityLabels'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import { getDuplicateIdentitiesByScopeId } from './assignmentDuplicateIdentities'
import { FocusedAssignmentScope, getAbilityRowDragId } from './assignmentDnd'
import { getAssignmentUsageCountByIdentity } from './assignmentUsageCounts'
import SpellIcon from './SpellIcon'
import AssignmentTypeIcon from './AssignmentTypeIcon'
import TeamPlanAssignmentDialog from './TeamPlanAssignmentDialog'
import {
  buildAssignmentPhaseTimestampBadgeData,
  PhaseDivider,
} from './AssignmentPhaseTimestampBadge'
import { cn } from '@/lib/utils'
import { EncounterAbilityRow } from '../hooks/useTeamPlanComputedData'
import { CUSTOM_ABILITY_SPELL_ID } from '../utils/encounterAssignmentRows'
import AssignmentDescriptionContent, {
  AssignmentDescriptionControl,
} from './AssignmentDescriptionField'

type Scope = {
  id: string
  label: string
  castIndexes: number[]
  castGroups: number[][]
}

type PaletteEntity = {
  id: string
  entity: RaidPlanAssignmentEntity
  label: ReactNode
  category:
    | 'collection'
    | 'group'
    | 'specific_character'
    | 'by_role'
    | 'by_class'
}

type Props = {
  row: EncounterAbilityRow
  t: TFunction
  canEdit: boolean
  encounterId: number | undefined
  difficulty: Difficulty
  owner: RaidPlanOwner
  basePath: string
  openRaidPlanAbilityKey: string | null
  setOpenRaidPlanAbilityKey: (abilityKey: string | null) => void
  setConfirmRemoveAbilityKey: (abilityKey: string | null) => void
  setAssignmentDialogOpen: (open: boolean) => void
  focusedScope: FocusedAssignmentScope | null
  onFocusScope: (scope: FocusedAssignmentScope | null) => void
  popupScope: { abilityKey: string; scopeId: string } | null
  onPopupScopeChange: (
    scope: { abilityKey: string; scopeId: string } | null,
  ) => void
  showSidebar: boolean
  paletteEntities: PaletteEntity[]
  paletteLabelByIdentity: Map<string, ReactNode>
  memberInfoByCharacterId: Record<number, { name: string; className: string }>
  specializationById: Record<
    number,
    {
      name: string
      icon: string
      classId: number
      className: string
    }
  >
  storedAbility?: RaidPlanEncounterAssignmentAbility
  updateAbility: (
    abilityKey: RaidPlanEncounterAssignmentAbilityKey,
    spellId: number,
    castCount: number,
    assignmentGroups: number,
    updater: (ability: RaidPlanEncounterAssignmentAbility) => void,
    pushHistory?: boolean,
  ) => void
  onCommit: () => void
  removeAbility: (abilityKey: RaidPlanEncounterAssignmentAbilityKey) => void
  phaseDividers: PhaseDivider[]
}

const TeamPlanAssignmentRow = ({
  row,
  t,
  canEdit,
  encounterId,
  difficulty,
  owner,
  basePath,
  openRaidPlanAbilityKey,
  setOpenRaidPlanAbilityKey,
  setConfirmRemoveAbilityKey,
  setAssignmentDialogOpen,
  focusedScope,
  onFocusScope,
  popupScope,
  onPopupScopeChange,
  showSidebar,
  paletteEntities,
  paletteLabelByIdentity,
  memberInfoByCharacterId,
  specializationById,
  storedAbility,
  updateAbility,
  onCommit,
  removeAbility,
  phaseDividers,
}: Props) => {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: getAbilityRowDragId(row.abilityKey),
    disabled: !canEdit,
  })
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const isCustomAbility = row.isCustom
  const normalizedAssignmentGroups = getEffectiveAssignmentGroups(
    row.assignmentGroups,
    storedAbility?.assignment_groups,
  )
  const abilityAssignment: RaidPlanEncounterAssignmentAbility = {
    spell_id: row.spellId,
    casts: Array.from({ length: row.entries.length }, (_, castIndex) =>
      normalizeAssignmentCast(
        storedAbility?.casts?.[castIndex],
        normalizedAssignmentGroups,
      ),
    ),
    assignment_groups: storedAbility?.assignment_groups,
    expected_entities: storedAbility?.expected_entities,
    assignment_type: storedAbility?.assignment_type,
    title: storedAbility?.title,
    description: storedAbility?.description,
    message: storedAbility?.message,
    tts_message: storedAbility?.tts_message,
    cast_overrides: storedAbility?.cast_overrides,
    rgb: storedAbility?.rgb,
    raid_plan_link: storedAbility?.raid_plan_link,
    visual_id: storedAbility?.visual_id,
    visual_screen_ids: storedAbility?.visual_screen_ids,
    view_mode: storedAbility?.view_mode,
    custom_grouping: getEffectiveAbilityCustomGrouping(
      storedAbility?.spell_id ?? row.spellId,
      storedAbility?.custom_grouping,
    ),
  }
  const effectiveAssignmentGroups = getEffectiveAssignmentGroups(
    row.assignmentGroups,
    abilityAssignment.assignment_groups,
  )
  const abilityViewMode: AssignmentViewMode = getEffectiveAssignmentViewMode(
    abilityAssignment.view_mode,
  )
  const abilityCustomGrouping = getEffectiveAbilityCustomGrouping(
    abilityAssignment.spell_id,
    abilityAssignment.custom_grouping,
  )
  const scopes: Scope[] = getAssignmentScopes(
    row.entries,
    abilityViewMode,
    t,
    abilityCustomGrouping,
  )
  const duplicateIdentitiesByScopeId = getDuplicateIdentitiesByScopeId({
    ability: abilityAssignment,
    scopes,
    assignmentGroups: effectiveAssignmentGroups,
    viewMode: abilityViewMode,
  })
  const assignmentUsageCountByIdentity = getAssignmentUsageCountByIdentity({
    ability: abilityAssignment,
    scopes,
    assignmentGroups: effectiveAssignmentGroups,
  })
  const hasAssignedEntities = abilityAssignment.casts.some((cast) =>
    normalizeAssignmentCast(cast, effectiveAssignmentGroups).boxes.some(
      (box) => normalizeAssignmentBox(box).entities.length > 0,
    ),
  )
  const iconInteractionProps = canEdit
    ? ({ ...attributes, ...listeners } as HTMLAttributes<HTMLSpanElement>)
    : undefined
  const noteSettingsLabel = abilityAssignment.title ?? row.name
  const noteSettingsPlaceholderText =
    abilityAssignment.message?.trim() ||
    `${getSoakPrefix(noteSettingsLabel)}${noteSettingsLabel}`
  const noteSettingsPlaceholder = {
    message: noteSettingsPlaceholderText,
    ttsMessage: noteSettingsPlaceholderText,
  }
  const getEntityLabel = (entity: RaidPlanAssignmentEntity) =>
    paletteLabelByIdentity.get(getAssignmentEntityIdentity(entity)) ??
    getAssignmentEntityLabel(t, entity)
  const scopeComputations = scopes.map((scope) => {
    const representativeCast = normalizeAssignmentCast(
      abilityAssignment.casts[scope.castIndexes[0]],
      effectiveAssignmentGroups,
    )
    const scopeBoxes = Array.from(
      { length: effectiveAssignmentGroups },
      (_, boxIndex) =>
        normalizeAssignmentBox(representativeCast.boxes[boxIndex]),
    )
    const scopeEntitiesByGroup = scopeBoxes.map((box) => box.entities)
    const noteSettingsByGroup = scopeBoxes.map((box) => ({
      message: box.message,
      ttsMessage: box.tts_message,
      expectedEntities: box.expected_entities,
    }))
    const flatScopeEntities = scopeEntitiesByGroup.flat()
    return {
      scope,
      duplicateIdentities:
        duplicateIdentitiesByScopeId[scope.id] ?? new Set<string>(),
      scopeEntitiesByGroup,
      noteSettingsByGroup,
      flatScopeEntities,
    }
  })
  const entitiesByScopeId = scopeComputations.reduce<
    Record<string, RaidPlanAssignmentEntity[]>
  >((acc, { scope, flatScopeEntities }) => {
    acc[scope.id] = flatScopeEntities
    return acc
  }, {})
  const raidPlanPlayers = scopeComputations
    .flatMap(({ flatScopeEntities }) => flatScopeEntities)
    .flatMap<RaidstratsPlayer>((entity) => {
      if (entity.kind !== 'member') {
        return []
      }
      const memberInfo = memberInfoByCharacterId[entity.character_id]
      const specialization = entity.spec_id
        ? specializationById[entity.spec_id]
        : undefined
      const className = specialization?.className ?? memberInfo?.className
      const name = memberInfo?.name ?? entity.label
      if (!className || !name) {
        return []
      }
      return [{ name, className }]
    })
  const dedupedRaidPlanPlayers = Array.from(
    new Map(
      raidPlanPlayers.map((player) => [
        `${player.name}|${player.className}`,
        player,
      ]),
    ).values(),
  )
  const { visualPlan, visualRaidPlan } = useAbilityVisualPlanData({
    owner,
    ability: abilityAssignment,
    scopes,
    entitiesByScopeId,
    entries: row.entries,
    phaseDividers,
    specializationById,
    memberInfoByCharacterId,
    raidPlanPlayers: dedupedRaidPlanPlayers,
    getEntityLabel,
  })

  return (
    <div
      ref={setNodeRef}
      className='flex flex-col gap-2 mb-8'
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
        opacity: isDragging ? 0.8 : 1,
      }}
    >
      <div className='flex items-center justify-between gap-2 flex-wrap'>
        {isCustomAbility ? (
          <div className='flex items-center gap-2 text-xl min-w-0'>
            <span
              ref={setActivatorNodeRef}
              {...iconInteractionProps}
              className={cn(
                'inline-flex',
                canEdit ? 'cursor-grab active:cursor-grabbing' : undefined,
              )}
            >
              <SpellIcon
                spellId={CUSTOM_ABILITY_SPELL_ID}
                className='w-8 h-8 rounded border border-border'
              />
            </span>
            <AssignmentTypeIcon
              assignmentType={abilityAssignment.assignment_type}
            />
            <span className='truncate'>
              {abilityAssignment.title ?? row.name}
            </span>
          </div>
        ) : (
          <div className='flex items-center gap-2 text-xl min-w-0'>
            <span
              ref={setActivatorNodeRef}
              {...iconInteractionProps}
              className={cn(
                'inline-flex',
                canEdit ? 'cursor-grab active:cursor-grabbing' : undefined,
              )}
            >
              <SpellIcon
                spellId={row.spellId}
                className='w-8 h-8 rounded border border-border'
              />
            </span>
            <AssignmentTypeIcon
              assignmentType={abilityAssignment.assignment_type}
            />
            <a
              href={`https://wowhead.com/spell=${row.spellId}`}
              target='_blank'
              rel='noreferrer'
              className='truncate min-w-0'
              style={{ color: row.color }}
            >
              {abilityAssignment.title ?? row.name}
            </a>
          </div>
        )}
        {canEdit ? (
          <div className='flex items-center gap-2 flex-wrap justify-end'>
            <AssignmentDescriptionControl
              description={abilityAssignment.description}
              canEdit={canEdit}
              isEditing={isEditingDescription}
              onStartEdit={() => setIsEditingDescription(true)}
              className='h-10'
            />
            <Tabs
              value={abilityViewMode}
              onValueChange={(value) =>
                updateAbility(
                  row.abilityKey,
                  row.spellId,
                  row.entries.length,
                  effectiveAssignmentGroups,
                  (ability) => {
                    ability.view_mode = value as AssignmentViewMode
                  },
                  true,
                )
              }
            >
              <TabsList>
                <TabsTrigger value='all_together'>
                  {t('dashboard.team.plans.assignments.view-modes.all')}
                </TabsTrigger>
                <TabsTrigger value='alternating'>
                  {t('dashboard.team.plans.assignments.view-modes.alternating')}
                </TabsTrigger>
                <TabsTrigger value='all_separate'>
                  {t('dashboard.team.plans.assignments.view-modes.separate')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <TeamPlanAssignmentDialog
              open={openRaidPlanAbilityKey === row.abilityKey}
              onOpenChange={(open) =>
                setOpenRaidPlanAbilityKey(open ? row.abilityKey : null)
              }
              row={row}
              ability={abilityAssignment}
              spellName={abilityAssignment.title ?? row.name}
              defaultTitle={
                isCustomAbility
                  ? t('dashboard.team.plans.assignments.custom')
                  : row.name
              }
              canEdit={canEdit}
              assignmentGroups={effectiveAssignmentGroups}
              onUpdate={(updater, pushHistory) =>
                updateAbility(
                  row.abilityKey,
                  row.spellId,
                  row.entries.length,
                  effectiveAssignmentGroups,
                  updater,
                  pushHistory,
                )
              }
              onCommit={onCommit}
            />
            <AbilityVisualPopover
              ability={abilityAssignment}
              encounterId={encounterId}
              difficulty={difficulty}
              owner={owner}
              basePath={basePath}
              canEdit={canEdit}
              onUpdate={(updater, pushHistory) =>
                updateAbility(
                  row.abilityKey,
                  row.spellId,
                  row.entries.length,
                  effectiveAssignmentGroups,
                  updater,
                  pushHistory,
                )
              }
              onCommit={onCommit}
              t={t}
            />
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                if (!hasAssignedEntities) {
                  removeAbility(row.abilityKey)
                  return
                }
                setConfirmRemoveAbilityKey(row.abilityKey)
              }}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>
        ) : null}
      </div>

      <AssignmentDescriptionContent
        description={abilityAssignment.description}
        isEditing={isEditingDescription}
        clearTarget='this assignment'
        onStopEdit={() => setIsEditingDescription(false)}
        onSave={async (html) => {
          updateAbility(
            row.abilityKey,
            row.spellId,
            row.entries.length,
            effectiveAssignmentGroups,
            (ability) => {
              ability.description = html || undefined
            },
          )
          setIsEditingDescription(false)
        }}
      />

      {scopes.length === 0 ? (
        <div className='flex items-center gap-1 text-sm text-muted-foreground'>
          <span>This ability has no casts.</span>
          {canEdit ? (
            <>
              {' '}
              <span>Add casts in the </span>
              <Button
                type='button'
                variant='outline'
                className='px-1.5'
                size='xs'
                onClick={() => setOpenRaidPlanAbilityKey(row.abilityKey)}
              >
                settings
              </Button>
            </>
          ) : null}
        </div>
      ) : null}

      {visualPlan || visualRaidPlan ? (
        <TeamPlanAssignmentVisualCard
          label={t('dashboard.team.plans.assignments.visual-section')}
          visualPlan={visualPlan}
          visual={visualRaidPlan}
        />
      ) : null}

      {scopeComputations.map(
        ({
          scope,
          duplicateIdentities,
          scopeEntitiesByGroup,
          noteSettingsByGroup,
        }) => (
          <TeamPlanAssignmentScopeCard
            key={scope.id}
            scope={scope}
            spellId={row.spellId}
            castTimestamps={scope.castGroups.map((castGroup) =>
              buildAssignmentPhaseTimestampBadgeData({
                castIndexes: castGroup,
                entries: row.entries,
                phaseDividers,
              }),
            )}
            entitiesByGroup={scopeEntitiesByGroup}
            assignmentGroups={effectiveAssignmentGroups}
            abilityExpectedEntities={abilityAssignment.expected_entities}
            canEdit={canEdit}
            paletteEntities={paletteEntities}
            assignmentUsageCountByIdentity={assignmentUsageCountByIdentity}
            onSave={(nextEntitiesByGroup) =>
              updateAbility(
                row.abilityKey,
                row.spellId,
                row.entries.length,
                effectiveAssignmentGroups,
                (ability) => {
                  scope.castIndexes.forEach((castIndex) => {
                    const cast = normalizeAssignmentCast(
                      ability.casts[castIndex],
                      effectiveAssignmentGroups,
                    )
                    nextEntitiesByGroup.forEach((nextEntities, boxIndex) => {
                      cast.boxes[boxIndex] = {
                        ...normalizeAssignmentBox(cast.boxes[boxIndex]),
                        entities: nextEntities.map((entity) => ({
                          ...entity,
                          id: entity.id ?? uuid(),
                        })),
                      }
                    })
                    ability.casts[castIndex] = cast
                  })
                },
                true,
              )
            }
            assignLabel={t('dashboard.team.plans.assignments.assign')}
            abilityViewMode={abilityViewMode}
            duplicateIdentities={duplicateIdentities}
            getEntityLabel={getEntityLabel}
            groupLabel={(count) =>
              t('dashboard.team.plans.assignments.group-label', { count })
            }
            onAssignmentDialogOpenChange={setAssignmentDialogOpen}
            abilityKey={row.abilityKey}
            focusedScope={focusedScope}
            onFocusScope={onFocusScope}
            popupOpen={
              popupScope?.abilityKey === row.abilityKey &&
              popupScope?.scopeId === scope.id
            }
            onPopupOpenChange={(open) =>
              onPopupScopeChange(
                open ? { abilityKey: row.abilityKey, scopeId: scope.id } : null,
              )
            }
            showSidebar={showSidebar}
            description={
              storedAbility?.casts?.[scope.castIndexes[0]]?.description
            }
            onDescriptionSave={async (html) => {
              updateAbility(
                row.abilityKey,
                row.spellId,
                row.entries.length,
                effectiveAssignmentGroups,
                (ability) => {
                  scope.castIndexes.forEach((castIndex) => {
                    const cast = normalizeAssignmentCast(
                      ability.casts[castIndex],
                      effectiveAssignmentGroups,
                    )
                    cast.description = html || undefined
                    ability.casts[castIndex] = cast
                  })
                },
              )
            }}
            noteSettingsByGroup={noteSettingsByGroup}
            noteSettingsPlaceholder={noteSettingsPlaceholder}
            abilityMessage={abilityAssignment.message}
            abilityLabel={noteSettingsLabel}
            onNoteSettingsSave={(boxIndex, next) =>
              updateAbility(
                row.abilityKey,
                row.spellId,
                row.entries.length,
                effectiveAssignmentGroups,
                (ability) => {
                  scope.castIndexes.forEach((castIndex) => {
                    const cast = normalizeAssignmentCast(
                      ability.casts[castIndex],
                      effectiveAssignmentGroups,
                    )
                    const box = normalizeAssignmentBox(cast.boxes[boxIndex])
                    box.message = next.message
                    box.tts_message = next.ttsMessage
                    box.expected_entities = next.expectedEntities
                    cast.boxes[boxIndex] = box
                    ability.casts[castIndex] = cast
                  })
                },
                true,
              )
            }
          />
        ),
      )}
    </div>
  )
}

export default TeamPlanAssignmentRow
